import { useMachine } from '@xstate/react';
import { assign, fromPromise, setup } from 'xstate';
import { DateObject } from '@uplift/services';
import { AppointmentRecurring, AvailabilityDayOfWeekInterval, AvailabilityTimeOfDayInterval, getInsurances, getMatchPreference, getUserProfile } from '@uplift/api';
import { useHistory } from 'react-router';
import { useCurrentuser } from '@uplift/contexts';
import { AddressType } from '@uplift/types';
import { SELF_PAY_ID } from '../../onboarding/utils';

const WHAT_PAGE_REASON = 'notHappyOfCare';
const NEW_ADDRESS_PAGE_REASON = 'movedAndTherapistNotSupport';

export type RematchingStateMachineContext = {
  isNew: boolean;
  matchId: string;
  matchProviderId?: string;
  meId: string;
  rematchReasons: string[];

  insuranceId: string | null;
  specialtyIds: string[];
  identityFilters: {
    genderIds: string[];
    faithIds: string[];
    ethnicityIds: string[];
  };
  experienceFilters: {
    communityIds: string[];
    faithIds: string[];
    ethnicityIds: string[];
  };
  availabilityTimeOfDay: AvailabilityTimeOfDayInterval[];
  availabilityDayOfWeek: AvailabilityDayOfWeekInterval[];
  ageId: string | null;
  stateId: string | null;

  selectedSlot: {
    fromDatetime: DateObject | string;
    recurring: AppointmentRecurring;
  } | null;
  newProvider: {
    id: string;
    first_name: string;
    last_name: string;
    avatar_url?: string;
    preferred_name?: string;
  } | null;
  details?: string;
  hasNoTherapist?: boolean;
  newAddress: AddressType | null;
};

export type NewAddressNextData = {
  address: null;
};

export type MatchesUpdateFiltersData = {
  specialtyIds: string[];
  identityFilters: {
    genderIds: string[];
    faithIds: string[];
    ethnicityIds: string[];
  };
  experienceFilters: {
    communityIds: string[];
    faithIds: string[];
    ethnicityIds: string[];
  };
  availabilityTimeOfDay: AvailabilityTimeOfDayInterval[];
  availabilityDayOfWeek: AvailabilityDayOfWeekInterval[];
};

export type WhatNextData = {
  specialtyIds: string[];
};

export type ProvidersNextData = {
  newProvider: RematchingStateMachineContext['newProvider'];
  selectedSlot: RematchingStateMachineContext['selectedSlot'];
};

export type LoadActorReturnType = Pick<RematchingStateMachineContext,
| 'ageId'
| 'stateId'
| 'identityFilters'
| 'experienceFilters'
| 'specialtyIds'
| 'insuranceId'
>;

type Event =
    | { type: 'WHY.NEXT'; data: { rematchReasons: string[] } }
    | { type: 'MORE_DETAILS.NEXT'; data: { details: string } }
    | { type: 'NEW_ADDRESS.NEXT'; data: NewAddressNextData }
    | { type: 'WHAT.NEXT'; data: WhatNextData }
    | { type: 'PROVIDERS.NEXT'; data: ProvidersNextData }
    | { type: 'PROVIDERS.UPDATE_FILTERS'; data: MatchesUpdateFiltersData }
    | { type: 'BACK' };

export type UseRematchingMachineProps = {
  latestMatchId: string;
  matchId: string;
  matchProviderId?: string;
};

export const useRematchingMachine = (props: UseRematchingMachineProps) => {
  const me = useCurrentuser();
  const history = useHistory();

  const isNew = props.matchId === 'new';
  const matchId = isNew ? props.latestMatchId : props.matchId;

  return useMachine(machine.provide({
    actions: {
      exit: () => history.goBack()
    },
  }), {
    input: {
      matchId: matchId,
      isNew: isNew,
      matchProviderId: props.matchProviderId,
      meId: me.id,
    }
  });
};

const machine = setup({
  types: {
    context: {} as RematchingStateMachineContext,
    input: {} as Pick<RematchingStateMachineContext, 'matchId' | 'isNew' | 'matchProviderId' | 'meId'>,
    events: {} as Event,
  },
  actors: {
    load: fromPromise<LoadActorReturnType, Pick<RematchingStateMachineContext, 'matchId' | 'meId'>>(async ({ input }) => {
      const { data: { insurances } } = await getInsurances(input.meId);
      const { data: userProfile } = await getUserProfile(input.meId);
      const { data: matchPreference } = await getMatchPreference(input.matchId);

      const insuranceId = insurances[0]?.companyId || SELF_PAY_ID;

      return {
        ageId: matchPreference.ageGroupId,
        stateId: userProfile.address?.stateId || matchPreference.locationState,
        specialtyIds: matchPreference.visitReasonIds,
        insuranceId: insuranceId,
        identityFilters: {
          faithIds: matchPreference.faithIds,
          ethnicityIds: matchPreference.ethnicityIds,
          genderIds: matchPreference.genderIds,
        },
        experienceFilters: {
          faithIds: matchPreference.communityExpertiseFaithIds,
          ethnicityIds: matchPreference.communityExpertiseEthnicityIds,
          communityIds: matchPreference.communityExpertiseGeneralIds,
        },
      };
    })
  },
  actions: {
    exit: () => {
      return;
    },
  },
  guards: {
    isNew: ({ context }) => context.matchId === 'new',
    includeMoreDetailPage: ({ event, context }) => {
      let rematchReasons = context.rematchReasons;

      if (event.type === 'WHY.NEXT') {
        rematchReasons = event.data.rematchReasons;
      }

      if (!rematchReasons.length) {
        return false;
      }

      return rematchReasons.includes(WHAT_PAGE_REASON);
    },
    includeNewAddressPage: ({ event, context }) => {
      let rematchReasons = context.rematchReasons;

      if (event.type === 'WHY.NEXT') {
        rematchReasons = event.data.rematchReasons;
      }

      if (!rematchReasons.length) {
        return false;
      }

      return rematchReasons.includes(NEW_ADDRESS_PAGE_REASON);
    }
  }
}).createMachine({
  id: 'rematching',
  context: ({ input }) => ({
    meId: input.meId,
    isNew: input.isNew,
    matchId: input.matchId,
    matchProviderId: input.matchProviderId,
    reasons: [],
    specialtyIds: [],
    rematchReasons: [],
    identityFilters: {
      genderIds: [],
      faithIds: [],
      ethnicityIds: [],
    },
    experienceFilters: {
      communityIds: [],
      faithIds: [],
      ethnicityIds: [],
    },
    availabilityTimeOfDay: [],
    availabilityDayOfWeek: [],
    stateId: null,
    ageId: null,
    insuranceId: null,
    selectedSlot: null,
    newProvider: null,
    newAddress: null,
  }),
  initial: 'INITIAL',
  states: {
    'INITIAL': {
      invoke: {
        src: 'load',
        input: ({ context }) => ({ matchId: context.matchId, meId: context.meId }),
        onDone: [
          {
            target: 'WHAT',
            guard: 'isNew',
            actions: assign(({ event }) => ({ ...event.output }))
          },
          {
            target: 'WHY',
            actions: assign(({ event }) => ({ ...event.output }))
          }
        ]
      }
    },
    'WHY': {
      on: {
        BACK: {
          actions: 'exit',
        },
        'WHY.NEXT': [
          {
            target: 'MORE_DETAILS',
            guard: 'includeMoreDetailPage',
            actions: assign({ rematchReasons: ({ event }) => event.data.rematchReasons }),
          },
          {
            target: 'NEW_ADDRESS',
            guard: 'includeNewAddressPage',
            actions: assign({ rematchReasons: ({ event }) => event.data.rematchReasons }),
          },
          {
            target: 'WHAT',
            actions: assign({ rematchReasons: ({ event }) => event.data.rematchReasons }),
          },
        ],
      },
    },
    'MORE_DETAILS': {
      on: {
        BACK: {
          target: 'WHY',
        },
        'MORE_DETAILS.NEXT': [
          {
            target: 'NEW_ADDRESS',
            guard: 'includeNewAddressPage',
            actions: assign({ details: ({ event }) => event.data.details })
          },
          {
            target: 'WHAT',
            actions: assign({ details: ({ event }) => event.data.details })
          },
        ],
      },
    },
    'NEW_ADDRESS': {
      on: {
        BACK: [
          {
            target: 'MORE_DETAILS',
            guard: 'includeMoreDetailPage',
          },
          {
            target: 'WHY'
          }
        ],
        'NEW_ADDRESS.NEXT': {
          target: 'WHAT',
          actions: assign({ newAddress: ({ event }) => event.data.address })
        },
      }
    },
    'WHAT': {
      on: {
        BACK: [
          {
            actions: 'exit',
            guard: 'isNew',
          },
          {
            target: 'NEW_ADDRESS',
            guard: 'includeNewAddressPage',
          },
          {
            target: 'MORE_DETAILS',
            guard: 'includeMoreDetailPage',
          },
          {
            target: 'WHY'
          },
        ],
        'WHAT.NEXT': {
          target: 'PROVIDERS',
          actions: assign({ specialtyIds: ({ event }) => event.data.specialtyIds }),
        },
      },
    },
    'PROVIDERS': {
      on: {
        BACK: 'WHAT',
        'PROVIDERS.NEXT': {
          target: 'CONFIRM',
          actions: assign({
            newProvider: ({ event }) => event.data.newProvider,
            selectedSlot: ({ event }) => event.data.selectedSlot,
          }),
        },
        'PROVIDERS.UPDATE_FILTERS': {
          actions: assign({
            specialtyIds: ({ event }) => event.data.specialtyIds,
            identityFilters: ({ event }) => event.data.identityFilters,
            experienceFilters: ({ event }) => event.data.experienceFilters,
            availabilityTimeOfDay: ({ event }) => event.data.availabilityTimeOfDay,
            availabilityDayOfWeek: ({ event }) => event.data.availabilityDayOfWeek,
          })
        },
      },
    },
    'CONFIRM': {
      on: {
        BACK: 'PROVIDERS',
      },
    },
  },
});
